import { textureBaseColors } from './palette';
import { blockAppearance, type BlockAppearance } from './blockAppearance';
import { bakeModel, DIRECTIONS, type JavaModel, type ModelElement, type Point } from './javaModels';

function cube(from: Point,to: Point,texture: string,u=0,v=0,textureWidth=16): ModelElement {
  const [w,h,d]=to.map((n,i)=>n-from[i]);
  const uv={west:[u,v+d,u+d,v+d+h],north:[u+d,v+d,u+d+w,v+d+h],east:[u+d+w,v+d,u+2*d+w,v+d+h],south:[u+2*d+w,v+d,u+2*d+2*w,v+d+h],up:[u+d,v,u+d+w,v+d],down:[u+d+w,v,u+d+2*w,v+d]};
  return {from,to,faces:Object.fromEntries(DIRECTIONS.map(face=>[face,{texture,uv:uv[face].map(n=>n*16/textureWidth)}]))};
}
function blockCube(from: Point,to: Point,texture:string):ModelElement {
  return {from,to,faces:Object.fromEntries(DIRECTIONS.map(face=>[face,{texture,uv:[0,0,16,16]}]))};
}
const invisible=/^(air|cave_air|void_air|barrier|structure_void|light|light_block(?:_\d+)?|moving_piston|bubble_column)$/;
export function specialAppearance(rawName:string,properties:Record<string,string>={}):BlockAppearance|null {
  const name=rawName.replace(/^minecraft:/,'');
  const elements:ModelElement[]=[];
  let layer:BlockAppearance['layer']='solid', rotation=({north:0,east:90,south:180,west:270} as Record<string,number>)[properties.facing??'north']??0;
  if(invisible.test(name)) {
    // These blocks have no visible surface in the game.
  } else if(name==='water'||name==='lava') {
    const level=Math.max(0,Math.min(15,Number(properties.level??0)));
    elements.push(blockCube([0,0,0],[16,level>=8?16:(8-Math.min(7,level))*2-0.125,16],`minecraft:block/${name}_still`));
    layer=name==='water'?'translucent':'solid';
  } else if(name==='end_portal'||name==='end_gateway') {
    elements.push(blockCube([0,0,0],[16,name==='end_portal'?12:16,16],'minecraft:entity/end_portal/end_portal'));
  } else if(/(?:^|_)chest$/.test(name)) {
    let material=name.includes('ender')?'ender':name.includes('trapped')?'trapped':'normal';
    if(name.includes('copper')) material='copper'+(name.includes('oxidized')?'_oxidized':name.includes('weathered')?'_weathered':name.includes('exposed')?'_exposed':'');
    const paired=properties.type==='left'||properties.type==='right';
    if(paired) material+='_'+properties.type;
    const texture=`minecraft:entity/chest/${material}`;
    const x0=paired&&properties.type==='right'?0:1,x1=paired&&properties.type==='left'?16:15;
    elements.push(cube([x0,0,1],[x1,10,15],texture,0,19,64),cube([x0,9,1],[x1,14,15],texture,0,0,64),cube([7,7,0],[9,11,1],texture,0,0,64));
  } else if(/(?:^|_)shulker_box$/.test(name)) {
    const color=name==='shulker_box'?'':('_'+name.replace('_shulker_box',''));
    const texture=`minecraft:entity/shulker/shulker${color}`;
    elements.push(cube([0,0,0],[16,8,16],texture,0,28,64),cube([0,8,0],[16,16,16],texture,0,0,64));
  } else if(/^(skeleton|wither_skeleton|zombie|player|creeper|piglin|dragon)(?:_wall)?_(head|skull)$/.test(name)) {
    const type=name.replace(/_wall/,'').replace(/_(head|skull)$/,'');
    const textures:Record<string,string>={skeleton:'skeleton/skeleton',wither_skeleton:'skeleton/wither_skeleton',zombie:'zombie/zombie',creeper:'creeper/creeper',player:'player/wide/steve',piglin:'piglin/piglin',dragon:'enderdragon/dragon'};
    const texture='minecraft:entity/'+textures[type];
    const wall=name.includes('_wall_');
    elements.push(cube([4,wall?4:0,wall?8:4],[12,wall?12:8,wall?16:12],texture,0,0,type==='dragon'?256:64));
    if(type==='dragon') elements.push(cube([5,1,0],[11,5,5],texture,176,44,256));
    if(type==='piglin') elements.push(cube([2,5,5],[4,8,7],texture,51,6,64),cube([12,5,5],[14,8,7],texture,51,6,64));
    if(!wall) rotation=Number(properties.rotation??0)*22.5;
  } else if(name.endsWith('_banner')) {
    const wall=name.includes('_wall_');
    const z=wall?15:8;
    const cloth=blockCube([0,wall?-12:4,z-0.5],[16,wall?16:32,z+0.5],'minecraft:entity/banner/base');
    for(const face of Object.values(cloth.faces))face!.uv=[0.25,0.25,5.25,10.25];
    elements.push(cloth);
    elements.push(blockCube([0,wall?15:31,z-1],[16,wall?17:33,z+1],'minecraft:block/oak_planks'));
    if(!wall) elements.push(blockCube([7,0,7],[9,32,9],'minecraft:block/oak_planks'));
    if(!wall) rotation=Number(properties.rotation??0)*22.5;
  } else if(name==='conduit') {
    elements.push(cube([5,5,5],[11,11,11],'minecraft:entity/conduit/base',0,0,32));
  } else if(name==='decorated_pot') {
    elements.push(blockCube([1,0,1],[15,14,15],'minecraft:entity/decorated_pot/decorated_pot_side'));
    elements.push(cube([5,14,5],[11,16,11],'minecraft:entity/decorated_pot/decorated_pot_base',0,0,32));
  } else if(name.endsWith('copper_golem_statue')) {
    const suffix=name.includes('oxidized')?'_oxidized':name.includes('weathered')?'_weathered':name.includes('exposed')?'_exposed':'';
    const texture=`minecraft:entity/copper_golem/copper_golem${suffix}`;
    // Geometry dimensions and UV origins from Education's geometry.copper_golem.
    elements.push(cube([4,5,5],[12,11,11],texture,0,15,64),cube([4,11,3],[12,16,13],texture,0,0,64),
      cube([7,10,2],[9,13,4],texture,56,0,64),cube([7,16,7],[9,20,9],texture,37,8,64),
      cube([6,20,6],[10,24,10],texture,37,0,64),cube([1,2,6],[4,12,10],texture,36,16,64),
      cube([12,2,6],[15,12,10],texture,50,16,64),cube([4.1,0,6.01],[8.1,5,10.01],texture,0,27,64),
      cube([7.9,0,6],[11.9,5,10],texture,16,27,64));
  } else if(name==='chalkboard') {
    const type=properties.chalkboard_size??'0';
    const w=type==='2'?48:type==='1'?32:16,h=type==='2'?32:16;
    elements.push(blockCube([0,0,7],[w,h,9],'minecraft:block/dark_oak_planks'),blockCube([1,1,6.9],[w-1,h-1,7],'viewer:blackboard'));
    rotation=Number(properties.ground_sign_direction??properties.rotation??0)*22.5;
  } else return null;
  const model:JavaModel={elements};
  const quads=bakeModel(model,{model:'special',y:rotation},rawName,properties);
  if(name==='water') quads.forEach(q=>q.tint=0x3f76e4);
  if(name.endsWith('_banner'))for(const q of quads)if(q.texture==='minecraft:entity/banner/base')q.tint=textureBaseColors[name.replace('_wall','').replace('_banner','_wool')]??0xffffff;
  const original=blockAppearance(rawName,properties);
  return {...original,quads,cross:false,occludes:false,layer,modelSource:'special'};
}
